import { useMemo } from 'react';
import { LTSSService, ClientUtilization, Program } from '../types/ltss';
import { getProgramLimits } from '../components/Settings/ProgramLimits';

// Derive rate per hour from paid claims data for a program
function deriveRatePerHour(services: LTSSService[], program: Program): number {
  let totalPaid = 0;
  let totalHours = 0;

  services.forEach(service => {
    if (
      service.program === program &&
      service.amountPaid &&
      service.amountPaid > 0 &&
      service.serviceLength &&
      service.serviceLength > 0 &&
      (service.claimHistory === 'Original Claim Paid' || service.claimHistory === 'Adjustment Claim Paid')
    ) {
      totalPaid += service.amountPaid;
      totalHours += service.serviceLength;
    }
  });

  // Default to $25.60/hr (4 units/hr × $6.40/unit) if no data
  return totalHours > 0 ? totalPaid / totalHours : 25.60;
}

export function useClientUtilization(services: LTSSService[]): ClientUtilization[] {
  return useMemo(() => {
    const limits = getProgramLimits();
    const utilizationMap: Record<string, ClientUtilization> = {};

    // Pre-calculate hourly rates per program
    const programRates: Record<string, number> = {};
    const programs: Program[] = ['CFC', 'CO', 'CPAS', 'EPSDT', 'REM'];
    programs.forEach(program => {
      // Check if manually set in limits (convert from unit rate to hourly rate)
      const programLimit = limits[program];
      if (programLimit?.ratePerUnit !== null && programLimit?.ratePerUnit !== undefined) {
        const unitsPerHour = programLimit.unitsPerHour || 4;
        programRates[program] = programLimit.ratePerUnit * unitsPerHour;
      } else {
        programRates[program] = deriveRatePerHour(services, program);
      }
    });

    services.forEach(service => {
      const key = `${service.clientName}|${service.week}`;

      if (!utilizationMap[key]) {
        utilizationMap[key] = {
          clientName: service.clientName,
          clientId: service.clientId,
          program: service.program,
          week: service.week,
          authorizedHours: service.weeklyPosHours || 0,
          usedHours: 0,
          variance: 0,
          utilizationRate: 0,
          revenue: 0,
          serviceCount: 0,
          missedRevenue: 0,
        };
      }

      const u = utilizationMap[key];
      u.usedHours += service.serviceLength || 0;
      u.revenue += service.amountPaid || 0;
      u.serviceCount++;
    });

    // Calculate variance, utilization rate, and missed revenue
    return Object.values(utilizationMap).map(u => {
      const variance = u.usedHours - u.authorizedHours;
      const utilizationRate = u.authorizedHours > 0 
        ? Math.round((u.usedHours / u.authorizedHours) * 100) 
        : 0;
      
      // Missed revenue = unused hours × hourly rate (only if underutilized)
      const unusedHours = Math.max(0, u.authorizedHours - u.usedHours);
      const hourlyRate = programRates[u.program] || 25.60;
      const missedRevenue = unusedHours * hourlyRate;

      return {
        ...u,
        variance,
        utilizationRate,
        missedRevenue,
      };
    });
  }, [services]);
}
